"use client";

import { Header } from '@/pages/home/sections/header';
import { Footer } from '@/pages/home/sections/footer';
import { StandardCTA } from '@/components/standard-cta';
import { motion } from 'framer-motion';
import { Calendar, Clock, Headphones, Sparkles, BookOpen, Globe, Users, CheckCircle2, PhoneCall } from 'lucide-react';

const highlights = [
  {
    icon: Sparkles,
    title: 'Live Diagnostic Session',
    desc: 'Experience a 1:1 or small-group micro class that mirrors our on-boarding methodology and feedback loops.'
  },
  {
    icon: BookOpen,
    title: 'Track Recommendation',
    desc: 'Leave with a tailored syllabus recommendation across Qur\'an, Arabic or Islamic Studies based on current mastery.'
  },
  {
    icon: Globe,
    title: 'Global Time Zones',
    desc: 'Pick from optimized time slots to fit North America, Europe, MENA and Asia-Pacific households.'
  }
];

const benefits = [
  'Coaching-grade feedback on recitation, fluency or comprehension gaps.',
  'Placement clarity without guesswork using our proprietary diagnostic rubric.',
  'Structured study roadmap aligned with memorization, language or holistic goals.',
  'Parent & guardian consultation to set milestones and expectations early.',
  'Access to post-trial recordings and practice prompts to continue momentum.'
];

const steps = [
  {
    label: 'Step 01',
    title: 'Submit Learner Snapshot',
    text: 'Share goals, current level, preferred tracks and availability so we assign the right specialist instructor.'
  },
  {
    label: 'Step 02',
    title: 'Attend Guided Trial',
    text: 'Join a live 25-minute micro session with coaching feedback, progress metrics and Q&A for parents or learners.'
  },
  {
    label: 'Step 03',
    title: 'Receive Progress Blueprint',
    text: 'Get a placement recommendation, milestone map and tuition outline tailored to your mastery timeline.'
  }
];

const faqs = [
  {
    q: 'Who can attend the free trial session?',
    a: 'Learners ages 5+ plus parents or guardians seeking Qur\'an, Arabic or Islamic Studies pathways. We personalize instruction for youth, teens and adults.'
  },
  {
    q: 'How soon will we hear back?',
    a: 'Applications are reviewed within 12 business hours. We share qualified time slots and instructor match once confirmed.'
  },
  {
    q: 'Is there any obligation after the trial?',
    a: 'No. The session is designed to help you evaluate fit, understand expectations and receive expert guidance before enrolling.'
  }
];

export default function FreeTrialPage(){
  return (
    <div className="min-h-screen bg-[#FCF8F1]">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-24 lg:pt-48 lg:pb-32 overflow-hidden">
          <motion.div
            className="absolute inset-0 -z-10 opacity-60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          >
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#A8FF51]/35 blur-3xl" />
            <div className="absolute top-1/3 -left-32 w-96 h-96 rounded-full bg-[#245E51]/20 blur-3xl" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-white/30 blur-3xl" />
          </motion.div>
          <div className="w-[90%] max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <div className="inline-flex items-center px-3 py-1 text-sm font-semibold text-[#245E51] bg-[#A8FF51]/20 rounded-full border border-[#A8FF51]/30">
                Free Trial Session
              </div>
              <motion.h1
                initial={{ opacity:0, y:28 }}
                animate={{ opacity:1, y:0 }}
                transition={{ duration:.75, ease:[0.23,0.68,0.32,0.97] }}
                className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900"
              >
                Experience Dhikr Institute with a Guided Diagnostic Class
              </motion.h1>
              <motion.p
                initial={{ opacity:0, y:30 }}
                animate={{ opacity:1, y:0 }}
                transition={{ duration:.8, delay:.08, ease:[0.23,0.68,0.32,0.97] }}
                className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl leading-relaxed"
              >
                Test-drive our Qur\'an, Arabic and Islamic Studies methodology. Get real coaching feedback, placement clarity and an action plan rooted in authentic scholarship and modern pedagogy.
              </motion.p>
              <div className="mt-10 flex flex-wrap items-center gap-4 text-sm text-gray-700">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-white/60 shadow-sm"><Calendar className="h-4 w-4 text-[#245E51]" /> Flexible trial slots</span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-white/60 shadow-sm"><Clock className="h-4 w-4 text-[#245E51]" /> 25-minute session</span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-white/60 shadow-sm"><Headphones className="h-4 w-4 text-[#245E51]" /> Live coaching feedback</span>
              </div>
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="w-[90%] max-w-6xl mx-auto -mt-12 relative z-10">
          <div className="grid md:grid-cols-3 gap-6">
            {highlights.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.55, delay: index * 0.06, ease: [0.23, 0.68, 0.32, 0.97] }}
                className="rounded-3xl bg-white/85 backdrop-blur-xl border border-white/60 shadow-xl p-8"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#245E51]/10 flex items-center justify-center mb-5">
                  <item.icon className="h-6 w-6 text-[#245E51]" />
                </div>
                <h3 className="font-semibold text-gray-900 tracking-tight mb-3">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="py-28">
          <div className="w-[90%] max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, ease: [0.23, 0.68, 0.32, 0.97] }}
              className="space-y-6"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                What the Free Trial Unlocks
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
                Inspired by leading online academies like IQRA Network and Muslimeto, our diagnostic experience helps families make confident decisions grounded in transparent progress data and authentic teaching.
              </p>
              <ul className="space-y-4 text-sm sm:text-base text-gray-600">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#245E51] mt-1" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.75, ease: [0.23, 0.68, 0.32, 0.97] }}
              className="rounded-3xl bg-gradient-to-br from-[#245E51] to-[#0f362d] text-white shadow-2xl border border-white/20 p-10"
            >
              <div className="space-y-6">
                <h3 className="text-2xl font-bold">Personal Guidance for Every Household</h3>
                <p className="text-white/85 text-sm leading-relaxed">
                  Families appreciate seeing exactly how we blend soulful mentorship with technology-supported mastery. Our instructors spotlight quick wins and define habits that keep learners consistent.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
                    <span className="block text-xs uppercase tracking-wider text-white/70">Average Response</span>
                    <span className="mt-1 text-xl font-semibold">12 hours</span>
                  </div>
                  <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
                    <span className="block text-xs uppercase tracking-wider text-white/70">Families Served</span>
                    <span className="mt-1 text-xl font-semibold">2,300+</span>
                  </div>
                  <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
                    <span className="block text-xs uppercase tracking-wider text-white/70">Qualified Coaches</span>
                    <span className="mt-1 text-xl font-semibold">45+</span>
                  </div>
                  <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
                    <span className="block text-xs uppercase tracking-wider text-white/70">Student Satisfaction</span>
                    <span className="mt-1 text-xl font-semibold">4.9 / 5</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Application Form */}
        <section className="py-28">
          <div className="w-[90%] max-w-7xl mx-auto grid lg:grid-cols-[2fr_1fr] gap-12 xl:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, ease: [0.23, 0.68, 0.32, 0.97] }}
              className="space-y-8"
            >
              <div className="relative overflow-hidden rounded-4xl border border-white/60 bg-white shadow-2xl">
                <div className="absolute -top-32 -left-10 h-56 w-56 rounded-full bg-[#A8FF51]/20 blur-3xl" aria-hidden />
                <div className="absolute -bottom-32 -right-16 h-64 w-64 rounded-full bg-[#245E51]/10 blur-3xl" aria-hidden />
                <div className="relative p-8 sm:p-12 lg:p-14 space-y-8">
                  <div className="space-y-3">
                    <span className="inline-flex items-center gap-2 rounded-full border border-[#A8FF51]/40 bg-[#A8FF51]/15 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#245E51]">
                      Free Trial Application
                    </span>
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Apply for Your Free Trial Session</h2>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl">
                      Provide a quick learner snapshot so we can confirm availability. All applications receive a dedicated advisor follow-up within one business day.
                    </p>
                  </div>

                  <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <label className="group flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Parent / Guardian Name</span>
                        <input
                          required
                          className="h-12 rounded-xl border border-gray-200 bg-white/90 px-4 text-sm text-gray-900 shadow-inner focus:outline-none focus:ring-2 focus:ring-[#245E51]/40 focus:border-[#245E51]/40"
                          placeholder="Your full name"
                        />
                      </label>
                      <label className="group flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Email</span>
                        <input
                          type="email"
                          required
                          className="h-12 rounded-xl border border-gray-200 bg-white/90 px-4 text-sm text-gray-900 shadow-inner focus:outline-none focus:ring-2 focus:ring-[#245E51]/40 focus:border-[#245E51]/40"
                          placeholder="you@example.com"
                        />
                      </label>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <label className="group flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Phone / WhatsApp</span>
                        <input
                          required
                          className="h-12 rounded-xl border border-gray-200 bg-white/90 px-4 text-sm text-gray-900 shadow-inner focus:outline-none focus:ring-2 focus:ring-[#245E51]/40 focus:border-[#245E51]/40"
                          placeholder="Include country code"
                        />
                      </label>
                      <label className="group flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Student Name</span>
                        <input
                          required
                          className="h-12 rounded-xl border border-gray-200 bg-white/90 px-4 text-sm text-gray-900 shadow-inner focus:outline-none focus:ring-2 focus:ring-[#245E51]/40 focus:border-[#245E51]/40"
                          placeholder="Learner's name"
                        />
                      </label>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-5">
                      <label className="group flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Age</span>
                        <input
                          type="number"
                          min={3}
                          className="h-12 rounded-xl border border-gray-200 bg-white/90 px-4 text-sm text-gray-900 shadow-inner focus:outline-none focus:ring-2 focus:ring-[#245E51]/40 focus:border-[#245E51]/40"
                          placeholder="10"
                        />
                      </label>
                      <label className="group flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Preferred Track</span>
                        <select
                          className="h-12 rounded-xl border border-gray-200 bg-white/90 px-4 text-sm text-gray-900 shadow-inner focus:outline-none focus:ring-2 focus:ring-[#245E51]/40 focus:border-[#245E51]/40"
                        >
                          <option value="">Select a track</option>
                          <option value="quran">Qur'an Recitation & Tajweed</option>
                          <option value="memorization">Hifz / Memorization Support</option>
                          <option value="arabic">Arabic Language</option>
                          <option value="studies">Islamic Studies</option>
                          <option value="comprehensive">Comprehensive Path</option>
                        </select>
                      </label>
                      <label className="group flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Time Zone</span>
                        <input
                          required
                          className="h-12 rounded-xl border border-gray-200 bg-white/90 px-4 text-sm text-gray-900 shadow-inner focus:outline-none focus:ring-2 focus:ring-[#245E51]/40 focus:border-[#245E51]/40"
                          placeholder="e.g. GMT+3"
                        />
                      </label>
                    </div>

                    <label className="group flex flex-col gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Goals & Notes</span>
                      <textarea
                        rows={5}
                        className="rounded-2xl border border-gray-200 bg-white/90 px-4 py-3 text-sm text-gray-900 shadow-inner focus:outline-none focus:ring-2 focus:ring-[#245E51]/40 focus:border-[#245E51]/40 resize-none"
                        placeholder="Tell us about current level, previous study, scheduling preferences and goals."
                      />
                    </label>

                    <label className="group flex flex-col gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Preferred Trial Slot</span>
                      <input
                        className="h-12 rounded-xl border border-gray-200 bg-white/90 px-4 text-sm text-gray-900 shadow-inner focus:outline-none focus:ring-2 focus:ring-[#245E51]/40 focus:border-[#245E51]/40"
                        placeholder="Share 2-3 suitable days/times"
                      />
                    </label>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <button className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#245E51] to-[#1a4a3e] px-8 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl">
                        Submit Application
                      </button>
                      <p className="text-[11px] text-gray-500 leading-relaxed sm:max-w-xs">
                        We respect your privacy. Information is used solely for placement, onboarding and support communication in line with our data policy.
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.75, ease: [0.23, 0.68, 0.32, 0.97] }}
              className="space-y-10 lg:space-y-12"
            >
              <div className="rounded-3xl bg-white border border-gray-100 shadow-md p-8">
                <h3 className="font-semibold text-gray-900 tracking-tight mb-4 flex items-center gap-2"><Users className="h-5 w-5 text-[#245E51]" /> Who You\'ll Meet</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Certified Qur\'an and Arabic specialists with ijazah-level credentials, classroom experience and a heart for mentoring families across continents.
                </p>
              </div>
              <div className="rounded-3xl bg-white border border-gray-100 shadow-md p-8">
                <h3 className="font-semibold text-gray-900 tracking-tight mb-4 flex items-center gap-2"><PhoneCall className="h-5 w-5 text-[#245E51]" /> Post-Trial Support</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  An advisor will walk you through tuition plans, scheduling, technology setup and how to keep learners inspired between sessions.
                </p>
              </div>
              <div className="rounded-3xl bg-[#245E51] text-white shadow-xl border border-white/20 p-8 relative overflow-hidden">
                <div className="relative z-10 space-y-4">
                  <h3 className="text-xl font-bold">Need a quick orientation call first?</h3>
                  <p className="text-white/85 text-sm leading-relaxed">Book a 10-minute discovery call to explore tracks, tuition or partnership opportunities before applying.</p>
                  <a href="/contact" className="inline-flex items-center gap-2 text-sm font-semibold underline-offset-4 hover:underline">Speak with an advisor</a>
                </div>
                <div className="absolute -right-16 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
                <div className="absolute -left-10 -bottom-16 w-72 h-72 bg-black/10 rounded-full blur-3xl" />
              </div>
            </motion.div>
          </div>
        </section>

        <StandardCTA
          badge="Start With Confidence"
          titlePrefix="Your First"
          titleHighlight="Trial Session"
          titleSuffix="Is On Us"
          paragraph="Discover why families across the globe trust Dhikr Institute for Qur\'an mastery, Arabic fluency and transformative spiritual learning."
          primaryLabel="Secure My Spot"
          points={[
            { icon: 'assessment', label: 'Diagnostic placement included' },
            { icon: 'plan', label: 'Personalized progress blueprint' },
            { icon: 'rating', label: '4.9/5 learner satisfaction' }
          ]}
        />
      </main>
      <Footer />
    </div>
  );
}
